<script setup lang="ts">
import { inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { pointerMotionKey } from '../../composables/usePointerMotion'
import type { ExperienceQuality } from '../../lib/experience'
import type { HeroSceneController } from '../../scene/createHeroScene'

const props = defineProps<{
  quality: ExperienceQuality
  reducedMotion: boolean
}>()

const root = ref<HTMLElement | null>(null)
const canvas = ref<HTMLCanvasElement | null>(null)
const renderer = ref<'loading' | 'webgl' | 'fallback'>('loading')
const pulseActive = ref(false)
const sceneActive = ref(true)
const pointerMotion = inject(pointerMotionKey)
let controller: HeroSceneController | null = null
let observer: IntersectionObserver | undefined
let frame = 0
let pulseTimer = 0
let sceneVersion = 0
let unsubscribePointer: (() => void) | undefined

function updateScroll() {
  cancelAnimationFrame(frame)
  frame = requestAnimationFrame(() => {
    const height = root.value?.offsetHeight ?? window.innerHeight
    controller?.setScrollProgress(Math.min(1, window.scrollY / Math.max(height * 0.9, 1)))
  })
}

function resize() {
  controller?.resize()
}

function pulse() {
  window.clearTimeout(pulseTimer)
  pulseActive.value = true
  pulseTimer = window.setTimeout(() => {
    pulseActive.value = false
  }, 560)
  controller?.pulse()
}

async function mountScene() {
  const version = ++sceneVersion
  if (!canvas.value) return
  if (props.reducedMotion) {
    renderer.value = 'fallback'
    return
  }
  try {
    const { createHeroScene } = await import('../../scene/createHeroScene')
    const nextController = await createHeroScene({
      canvas: canvas.value,
      quality: props.quality,
      isMobile: window.matchMedia('(max-width: 700px)').matches,
      onReady: () => {
        if (version === sceneVersion) renderer.value = 'webgl'
      },
    })
    if (version !== sceneVersion) {
      nextController?.destroy(false)
      return
    }
    controller = nextController
    if (!controller) {
      renderer.value = 'fallback'
      return
    }
    const pointer = pointerMotion?.getSnapshot()
    if (pointer) controller.setPointer({ x: pointer.x, y: pointer.y })
    controller.setVisible(sceneActive.value)
    updateScroll()
  } catch (error) {
    if (version !== sceneVersion) return
    renderer.value = 'fallback'
    console.warn('[SE Universe] WebGL scene unavailable; using the SVG fallback.', error)
  }
}

onMounted(() => {
  void mountScene()
  unsubscribePointer = pointerMotion?.subscribe(({ x, y }) => {
    controller?.setPointer({ x, y })
  })
  window.addEventListener('scroll', updateScroll, { passive: true })
  window.addEventListener('resize', resize, { passive: true })
  observer = new IntersectionObserver(
    ([entry]) => {
      sceneActive.value = entry?.isIntersecting ?? false
      controller?.setVisible(sceneActive.value)
    },
    { rootMargin: '120px' },
  )
  if (root.value) observer.observe(root.value)
})

watch(
  () => [props.quality, props.reducedMotion] as const,
  async () => {
    sceneVersion += 1
    controller?.destroy(false)
    controller = null
    renderer.value = 'loading'
    await mountScene()
  },
)

onBeforeUnmount(() => {
  sceneVersion += 1
  cancelAnimationFrame(frame)
  window.clearTimeout(pulseTimer)
  observer?.disconnect()
  unsubscribePointer?.()
  window.removeEventListener('scroll', updateScroll)
  window.removeEventListener('resize', resize)
  controller?.destroy()
})
</script>

<template>
  <div
    ref="root"
    class="hero-scene"
    :class="{ 'is-pulsed': pulseActive }"
    data-testid="hero-core"
    :data-renderer="renderer === 'webgl' ? 'webgl' : 'fallback'"
    :data-scene-active="sceneActive ? 'true' : 'false'"
    @pointerdown="pulse"
  >
    <canvas ref="canvas" class="hero-scene__canvas" aria-hidden="true" />
    <div class="hero-scene__fallback" aria-hidden="true">
      <span class="hero-scene__orbit orbit--outer" />
      <span class="hero-scene__orbit orbit--middle" />
      <span class="hero-scene__orbit orbit--inner" />
      <svg class="hero-scene__mark" viewBox="0 0 430 430" role="presentation">
        <defs>
          <linearGradient id="se-core-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stop-color="#32e6ff" />
            <stop offset="0.52" stop-color="#2187ff" />
            <stop offset="1" stop-color="#8c6cff" />
          </linearGradient>
          <filter id="se-core-glow">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          class="mark-path mark-path--s"
          d="M184 98 C154 60 72 65 64 119 C57 166 169 151 174 207 C178 259 91 253 57 224"
        />
        <path
          class="mark-path mark-path--e"
          d="M203 84 H348 M225 84 V202 H330 M225 202 V320 H357"
        />
        <g class="mark-nodes" fill="#d7fbff">
          <circle cx="64" cy="119" r="5" />
          <circle cx="174" cy="207" r="5" />
          <circle cx="57" cy="224" r="5" />
          <circle cx="203" cy="84" r="5" />
          <circle cx="330" cy="202" r="5" />
          <circle cx="357" cy="320" r="5" />
        </g>
      </svg>
      <span class="hero-scene__pulse" />
    </div>
    <div class="hero-scene__coordinate coordinate--top">SE_CORE / 26.07</div>
    <div class="hero-scene__coordinate coordinate--bottom">X 31.240 · Y 121.490</div>
    <button class="hero-scene__trigger" type="button" aria-label="激活 SE 数字核心" @click="pulse">
      <span>ACTIVATE</span>
    </button>
  </div>
</template>
